/**
 * The post-event survey of handover rev7 §11.10, from the browser.
 *
 * Reached from U13 by a token, the same way §9.4's return page is reached by
 * `orders.public_token`. Both calls go to the `survey` Edge Function with the
 * **publishable** key on the `apikey` header; `supabaseFetch` is what enforces
 * that a secret key never does.
 *
 * Every one of the five questions is optional, so every field here is
 * optional, and an empty answer is still an answer — it is the response rate
 * §11.10 shows beside every verdict.
 */

import { supabaseFetch } from './api.ts'
import type { PublicConfig } from './env.ts'

export interface SurveyAnswers {
  guests_came?: number | null
  quantity_verdict?: string | null
  went_first?: string | null
  left_most?: string | null
  would_order_again?: boolean | null
}

export interface SurveyView {
  ref?: string
  event_name?: string | null
  event_date?: string
  customer_name?: string | null
  answered_at?: string | null
  revobox_out?: number | null
  verdicts?: { key: string; label: string }[]
  answers?: SurveyAnswers
}

export async function readSurvey(
  config: PublicConfig,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: boolean; survey?: SurveyView; message?: string }> {
  const response = await supabaseFetch(
    config.supabaseUrl,
    `/functions/v1/survey?token=${encodeURIComponent(token)}`,
    { method: 'GET', apiKey: config.supabasePublishableKey },
    fetchImpl,
  )
  return (await response.json().catch(() => ({ ok: false }))) as {
    ok: boolean
    survey?: SurveyView
    message?: string
  }
}

export async function submitSurvey(
  config: PublicConfig,
  token: string,
  answers: SurveyAnswers,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: boolean; message?: string }> {
  const response = await supabaseFetch(
    config.supabaseUrl,
    '/functions/v1/survey',
    {
      method: 'POST',
      apiKey: config.supabasePublishableKey,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token, ...answers }),
    },
    fetchImpl,
  )
  return (await response.json().catch(() => ({ ok: false }))) as { ok: boolean; message?: string }
}
